import React from "react";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import Popover, { type PopoverProps, type VisibleChangeReason } from "../../common/Popover/Popover";

export interface AttributePopoverAnchorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The element the panel positions against. It does not toggle the panel itself: the caller wires its own click. */
  anchor: React.ReactElement;
  placement?: PopoverProps["placement"];
  /**
   * Where focus goes when Escape closes the panel. Defaults to the anchor itself,
   * or the first focusable element inside it (e.g. the trigger button in a field).
   */
  returnFocus?: () => void;
  children: React.ReactNode;
}

const FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex='-1'])";

/**
 * Controlled click popover that floats the shared `AttributePopover` next to an
 * anchor. The anchor is a plain reference (`trigger={false}`) so callers keep
 * full control of their own click / mousedown handling, which matters inside
 * the rich text editor where mousedown must not steal the selection.
 * Outside press and Escape dismiss through `onOpenChange(false)`; after Escape,
 * focus goes back to the anchor (or `returnFocus`), so a keyboard user isn't
 * left on the page body.
 */
export const AttributePopoverAnchor = ({
  open,
  onOpenChange,
  anchor,
  placement = "bottom-start",
  returnFocus,
  children,
}: AttributePopoverAnchorProps) => {
  const anchorRef = React.useRef<HTMLElement | null>(null);

  const changeVisible = (next: boolean, _event?: Event, reason?: VisibleChangeReason) => {
    onOpenChange(next);
    if (next || reason !== "escape-key") return;
    if (returnFocus) {
      returnFocus();
      return;
    }
    const element = anchorRef.current;
    const target = element?.matches(FOCUSABLE) ? element : element?.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
  };

  return (
    <Popover
      visible={open}
      onVisibleChange={changeVisible}
      mode="click"
      interactable
      placement={placement}
      offset={6}
    >
      {(popover) => (
        <>
          <Popover.Reference
            asChild
            trigger={false}
            ref={anchorRef}
          >
            {anchor}
          </Popover.Reference>
          <Popover.Content
            asChild
            handleVisibility={false}
          >
            <FloatingPanel
              open={popover.visible}
              placement={popover.placement}
              unmountWhenHidden
            >
              {children}
            </FloatingPanel>
          </Popover.Content>
        </>
      )}
    </Popover>
  );
};

export default AttributePopoverAnchor;
